// FieldVision Admin - Map Upload API Functions
// Tilføj disse funktioner til eksisterende api.ts

// ============================================================================
// Types for Map Upload
// ============================================================================

export interface MapFile {
  mapId: number;
  orderId: number;
  marknr: string;
  cvr: string;
  mapType: string;
  mapFormat: string;
  version: string | null;
  blobPath: string;
  originalFilename: string;
  fileSize: number;
  uploadedAt: string;
  uploadedBy: string;
}

export interface MapUploadResponse {
  mapId: number;
  marknr: string;
  mapType: string;
  mapFormat: string;
  version: string | null;
  blobPath: string;
  fileSize: number;
  uploadedAt: string;
}

export interface MapsResponse {
  maps: MapFile[];
}

export interface DeleteMapResponse {
  success: boolean;
  message: string;
}

// ============================================================================
// Map Upload Functions
// ============================================================================

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3002';

function getToken(): string | null {
  return localStorage.getItem('fieldvision_token');
}

/**
 * Upload kort til ordre
 * Bruger FormData - sæt IKKE Content-Type header (browser sætter automatisk)
 */
export async function uploadMap(orderId: number, file: File): Promise<MapUploadResponse> {
  const token = getToken();
  const formData = new FormData();
  formData.append('file', file);

  const response = await fetch(`${API_BASE}/api/admin/orders/${orderId}/maps`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      // VIGTIGT: Sæt IKKE Content-Type - browser sætter automatisk for FormData
    },
    body: formData,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Upload fejlede');
  }

  return response.json();
}

/**
 * Hent kort for ordre
 */
export async function getMaps(orderId: number): Promise<MapsResponse> {
  const token = getToken();

  const response = await fetch(`${API_BASE}/api/admin/orders/${orderId}/maps`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Kunne ikke hente kort');
  }

  return response.json();
}

/**
 * Slet kort
 */
export async function deleteMap(mapId: number): Promise<DeleteMapResponse> {
  const token = getToken();

  const response = await fetch(`${API_BASE}/api/admin/maps/${mapId}`, {
    method: 'DELETE',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Kunne ikke slette kort');
  }

  return response.json();
}

/**
 * Download kort - åbner download i ny fane
 */
export async function downloadMap(mapId: number, filename: string): Promise<void> {
  const token = getToken();

  const response = await fetch(`${API_BASE}/api/admin/maps/${mapId}/download`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Kunne ikke downloade kort');
  }

  // Konverter response til blob og download
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
}
